import { useState } from 'react';
import type { JSX } from 'react';

import type { RevealSourceStatus } from '../../lib/revealStaging';

// bridge_ftue_offer / ADR-078: the raw N2K bridge's waiting-for-approval
// sub-beat, modeled on RevealCredentials.tsx's pending/skip pattern.
//
// When the operator opted into the secured-bridge offer (the opt-in row in
// NetworkRecommendation's gateway beat) the staged plan's `createCandidates`
// applies at the Devices→Watch transition, the boat creates the source, and
// the shipped connector self-provisions: an unauthenticated probe 401s, which
// raises a Signal K device-access request and holds the source in
// `connecting` with a reason naming the approval step (core
// `PENDING_APPROVAL_REASON`). This sub-beat surfaces that instruction.
//
// Unlike RevealCredentials there is no form to submit here — the approval
// happens externally, in Signal K admin — so the machine is just
// pending/skip: `pending` (revealStaging `bridgePendingApproval`) drives
// whether anything renders at all, and "Skip" fires NO verb (it just
// dismisses the sub-beat locally, mirroring RevealCredentials' Skip). Once
// the bridge connects, `bridgePendingApproval` naturally returns null (the
// source is no longer `connecting`), so the sub-beat disappears on its own —
// no separate "connected" state to render here.

export interface RevealSignalKAccessProps {
  /** The pending bridge status (revealStaging `bridgePendingApproval`), or
   *  null when nothing is currently awaiting Signal K approval. */
  pending: RevealSourceStatus | null;
}

export function RevealSignalKAccess({ pending }: RevealSignalKAccessProps): JSX.Element | null {
  const [skipped, setSkipped] = useState(false);

  if (pending === null || skipped) return null;

  return (
    <div className="reveal-signalk-access" data-testid="reveal-signalk-access">
      <p className="reveal-signalk-access__lead">
        The raw N2K bridge you opted into is waiting for approval in Signal K — it&apos;ll start
        streaming automatically once it&apos;s approved.
      </p>
      <p className="reveal-signalk-access__reason" data-testid="reveal-signalk-access-reason">
        {pending.reason}
      </p>
      <button
        type="button"
        className="btn btn-outline"
        onClick={() => setSkipped(true)}
        data-testid="reveal-signalk-access-skip"
      >
        Skip
      </button>
    </div>
  );
}
